import { useEffect, useRef, useState } from 'react'

import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { Bell, Sun, Moon, LogOut, KeyRound, UserRound } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { config } from '../../config'
import { ROUTES } from '../../constants'
import { useTheme } from '../../hooks/useTheme'
import { LanguageSwitcher, useLanguage } from '../../i18n'
import { isAuthenticated, logout as clearSession } from '../../pages/auth/login/auth'
import { getMyProfile } from '../../pages/profile/apis'
import { getUserDetails, remoteLogin } from '../../utils'
import { pusherInstance } from '../../utils/pusher'
import { NotificationDropdown } from '../components/NotificationDropdown'
import { useInboxCounts } from '../hooks/useInboxCounts'

function getInitials(name) {
  const parts = name?.trim()?.split(/\s+/).filter(Boolean) || []
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function Header({ onToggleCollapse, sidebarCollapsed }) {
  const { theme: mode, toggleTheme } = useTheme()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const bellRef = useRef(null)
  const [notifOpen, setNotifOpen] = useState(false)
  const profileQuery = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: getMyProfile,
    staleTime: 60_000,
  })
  const storedUser = getUserDetails()
  const initials = getInitials(profileQuery.data?.name || storedUser?.name) || 'A'

  // Live unread counts drive the bell badge. The hook shares its
  // queryKeys with NotificationDropdown so the two surfaces stay in sync
  // without duplicate network calls.
  const { counts: inboxCounts } = useInboxCounts()
  const totalUnread = inboxCounts.notificationsUnread + inboxCounts.alarmsUnread
  const handleProfileMenuOpen = (e) => setAnchorEl(e.currentTarget)
  const handleProfileMenuClose = () => setAnchorEl(null)

  const handleChangePassword = () => {
    handleProfileMenuClose()
    navigate(ROUTES.CHANGE_PASSWORD)
  }

  const handleProfile = () => {
    handleProfileMenuClose()
    navigate(ROUTES.PROFILE)
  }

  const handleLogout = () => {
    clearSession()
    handleProfileMenuClose()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  // Remote-login session: this tab was opened by the super-admin via the
  // Remote Login button. The bearer is per-tab (sessionStorage flag in
  // utils/remoteLogin.js) so the normal Logout item keeps working as-is
  // for any sibling tab that holds a real session.
  const isRemoteSession = remoteLogin.isActive()
  const handleReturnToAdmin = () => {
    handleProfileMenuClose()
    // Same teardown as a normal logout — disconnect Pusher, drop the
    // bearer + cached user data + global-settings cache, then drop the
    // per-tab flag.
    clearSession()
    remoteLogin.clear()
    const origin = config.app?.superAdminOrigin
    // window.close() is silently blocked by most browsers when the tab
    // wasn't script-opened, so redirect is the reliable cross-browser
    // fallback. The admin's customers page is the agreed hand-off point
    // (see MDG-215 acceptance criteria).
    window.location.href = origin ? `${origin.replace(/\/+$/, '')}/customers` : '/login'
  }

  // Sign in to Pusher as soon as the header is mounted for an
  // authenticated user. `pusherInstance()` is idempotent — every
  // subsequent caller gets the same singleton — so dropping it here
  // covers NotificationDropdown + any future channel subscribers without
  // each of them having to bootstrap the connection themselves.
  useEffect(() => {
    if (isAuthenticated()) {
      pusherInstance()
    }
  }, [])

  return (
    <AppBar className="header" position="sticky" elevation={0}>
      <Toolbar
        sx={{ justifyContent: 'space-between', minHeight: 72, px: { xs: 2, md: 3 }, gap: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <div
            onClick={onToggleCollapse}
            className={`cursor-pointer ${sidebarCollapsed ? 'rotate-180' : 'rotate-0'}`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: 'var(--header-icon-color)' }}
            >
              <g clipPath="url(#clip0_22_1710)">
                <path
                  d="M3 4H21V6H3V4ZM3 19H21V21H3V19ZM11 14H21V16H11V14ZM11 9H21V11H11V9ZM3 12.5L7 9V16L3 12.5Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_22_1710">
                  <rect width="24" height="24" fill="currentColor" />
                </clipPath>
              </defs>
            </svg>
          </div>
          {/* <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {title}
            </Typography>
          </Box> */}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <LanguageSwitcher />
          {/* <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.10)',
              px: 2,
              py: 0.75,
              bgcolor: 'rgba(255,255,255,0.08)'
            }}
          >
            <Typography color="text.secondary">🔍</Typography>
            <InputBase placeholder="Search..." sx={{ ml: 0.5, flex: 1 }} />
          </Box> */}
          <IconButton onClick={toggleTheme} sx={{ color: 'var(--header-icon-color)' }}>
            {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </IconButton>
          <IconButton
            ref={bellRef}
            onClick={() => setNotifOpen((prev) => !prev)}
            sx={{
              color: 'var(--header-icon-color)',
              '&:hover': { backgroundColor: 'rgba(59,130,246,0.08)' },
            }}
            aria-label="Notifications"
          >
            <Badge
              badgeContent={totalUnread > 0 ? totalUnread : null}
              max={99}
              color="error"
              overlap="circular"
              sx={{
                '& .MuiBadge-badge': {
                  fontWeight: 600,
                  fontSize: '0.6rem',
                  minWidth: 16,
                  height: 16,
                  boxShadow: '0 0 0 2px var(--header-bg)',
                },
              }}
            >
              <Bell size={20} />
            </Badge>
          </IconButton>
          <IconButton onClick={handleProfileMenuOpen} sx={{ color: 'var(--header-icon-color)' }}>
            <Badge variant="dot" color="success" overlap="circular">
              <Avatar sx={{ width: 40, height: 40, bgcolor: 'var(--primary)' }}>{initials}</Avatar>
            </Badge>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: { mt: 1, minWidth: 180, backgroundColor: 'var(--modal-bg-color)' },
            }}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <UserRound size={16} />
              </ListItemIcon>
              {t('Profile')}
            </MenuItem>
            <MenuItem onClick={handleChangePassword}>
              <ListItemIcon>
                <KeyRound size={16} />
              </ListItemIcon>
              {t('Change Password')}
            </MenuItem>
            {isRemoteSession && (
              <MenuItem onClick={handleReturnToAdmin}>
                <ListItemIcon>
                  <LogOut size={16} />
                </ListItemIcon>
                {t('Return to Admin')}
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogOut size={16} />
              </ListItemIcon>
              {t('Logout')}
            </MenuItem>
          </Menu>
        </Box>
        <NotificationDropdown
          open={notifOpen}
          anchorEl={bellRef.current}
          onClose={() => setNotifOpen(false)}
        />
      </Toolbar>
    </AppBar>
  )
}

export { Header }
export default Header
